type EmptyObject = Record<string, never>;

export enum Ga4TrackingEventType {
  GENERIC = 'generic_ga4_event',
  MEDALLIA = 'medallia'
}

/**
 *  Centralised documentation for all GA4 tracking events.
 *  In case of spec change, please update / add / remove in following event map.
 *  Needs to be maintained manually.
 *
 *  This file is excluded from Sonar to avoid 'Union types should not have too many elements' error,
 *  here we decided to keep big inline unions types for better readability
 */
export interface Ga4TrackingEventMap {
  registration: {
    event_method: 'zftc' | 'known contact';
  };
  download: {
    doc_type: string;
    doc_title: string;
    doc_url?: string;
  };
  contact_started: {
    event_method: string;
  };
  contact_submit: {
    event_method: string;
  };
  login: {
    event_method: 'top menu' | 'link';
  };
  login_started: {
    event_method: 'top menu' | 'link';
  };
  request_invoice: {
    event_method: 'single' | 'multiple';
  };
  add_to_favorites_click: {
    item_sku: string;
  };
  add_to_favorites_success: {
    item_sku: string;
  };
  editorials_cta: {
    button_type:
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'link'
      | 'hotspots'
      | 'QuickLinks'
      | 'contact_general'
      | 'open_overlay'
      | 'close_overlay'
      | 'related_products'
      | 'info_buttons_editorials'
      | 'flip_card_hover'
      | 'cordless'
      | 'applications'
      | 'tabs'
      | 'accordion'
      | 'selection_buttons_editorials'
      | 'page_section';
    button_text?: string;
  };
  video_start: {
    video_current_time: number;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  video_play: {
    video_current_time: string;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  video_progress: {
    video_percent: 10 | 20 | 40 | 60 | 80 | 90;
    video_current_time: number;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  video_pause: {
    video_current_time: number;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  video_seek: {
    video_current_time: number;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  video_complete: {
    video_current_time: number;
    video_title: string;
    video_provider: string;
    video_id: string;
  };
  search: {
    event_method: 'standard' | 'autosuggest range' | 'autosuggest category';
    search_term_typed: string;
    event_details?: 'search icon' | 'enter' | 'see all results';
    search_term_clicked?: string;
  };
  compare: {
    event_details: string;
  };
  filter: {
    event_method: 'category' | 'standard' | 'multiswitch' | 'advanced';
    filter_details?: string;
    filter_title?: string;
  };
  start_free_trial: EmptyObject;
  configure_products: {
    event_details:
      | 'choose other battery'
      | 'remove battery'
      | 'add battery'
      | 'choose other charger'
      | 'remove charger'
      | 'add charger'
      | 'add inserts'
      | 'add case'
      | 'remove case'
      | 'remove accessory'
      | 'choose other accessory'
      | 'add accessory';
  };
  email_cart_click: {
    item_sku: string;
  };
  // survey is the only medallia type
  survey: {
    trackingEvent: Ga4TrackingEventType.MEDALLIA;
    survey_name: string;
    survey_page: '1 out of 2' | '2 out of 2';
    survey_step: 'form displayed' | 'form closed without submit' | 'form submitted';
    survey_response?: string;
  };
  availability_check: {
    item_sku: string;
    event_details: string;
  };
  tools_on_demand_tab: {
    event_details: 'impression' | 'click';
  };
  tools_on_demand: {
    event_details: 'tool available' | 'price generated' | 'no tool' | 'alternative reco' | 'product click out';
    item_sku?: string;
  };
  write_review_started: EmptyObject;
  charger_swap_success: {
    event_details: string;
  };
  charger_swap_warning: {
    event_details: string;
  };
  reinvite_user_click: {
    error_type?: string;
  };
  reinvite_user_success: {
    error_type?: string;
  };
  reinvite_user_error: {
    error_type?: string;
  };
  print: {
    doc_type: string;
  };
  copy: EmptyObject;
  activate_software_click: {
    item_sku: string;
    error_type: string;
  };
  activate_software_success: {
    item_sku: string;
    error_type: string;
  };
  activate_software_declined: {
    item_sku: string;
    error_type: string;
  };
  activate_software_cancelled: {
    item_sku: string;
    error_type: string;
  };
  activate_software_pending: {
    item_sku: string;
    error_type: string;
  };
  activate_software_error: {
    item_sku: string;
    error_type: string;
  };
  activate_software_expired: {
    item_sku: string;
    error_type: string;
  };
  add_subscription: {
    event_method: 'recurring subscription' | 'first subscription';
  };
  add_user_click: {
    error_type: string;
  };
  add_user_invited: {
    error_type: string;
  };
  add_user_confirm: {
    error_type: string;
  };
  add_user_all_assigned: {
    error_type: string;
  };
  add_user_success: {
    error_type: string;
  };
  add_user_error: {
    error_type: string;
  };
  cancel_subscription_click: {
    error_type: string;
  };
  cancel_subscription_cancel: {
    error_type: string;
  };
  cancel_subscription_error: {
    error_type: string;
  };
  cancel_subscription_back: {
    error_type: string;
  };
  cancel_subscription_select_reason: {
    event_details: string;
  };
  cancel_subscription_confirm: {
    event_details: string;
    event_method: 'scheduled' | 'immediate';
  };
  cancel_subscription_success: {
    event_details: string;
    event_method: 'scheduled' | 'immediate';
  };
  toolpark: {
    event_method:
      | 'print_summary_page'
      | 'renew_fleet'
      | 'reorder'
      | 'repair'
      | 'report'
      | 'return'
      | 'loan'
      | 'return_peak'
      | 'return_single'
      | 'change_cost_center'
      | 'cost_center'
      | 'is_lost'
      | 'is_stolen';
    event_details:
      | 'repair'
      | 'return'
      | 'single'
      | 'multiple'
      | 'button'
      | 'overlay_button'
      | 'overlay_single'
      | 'cost'
      | 'order_tracking'
      | 'report_button'
      | 'report_single'
      | 'return_button return_button'
      | 'remove_entry';
    event_phase?: 'submit';
  };
  toolpark_success: {
    event_method: 'return_loan_pickup' | 'return_loan_summary' | 'reorder_labels';
    event_phase: 'submit';
    event_details?: 'submit';
  };
  subscription_center: {
    event_method:
      | 'back_to_homepage'
      | 'manage_billing'
      | 'manage_subscription'
      | 'manage_users'
      | 'new_contact'
      | 'agreement_sign'
      | 'remove_user'
      | 'back_to_subscriptions'
      | 'transfer_admin_rights';
    event_details?: 'cancel' | 'close_overlay' | 'declined' | 'pending' | 'error' | 'expired';
    item_sku?: string;
    button_type?: 'smartbar';
  };
  subscription_center_success: {
    event_method: 'new_contact' | 'agreement_sign' | 'remove_user' | 'transfer_admin_rights';
    item_sku?: string;
  };
  find_hilti_store: {
    event_method:
      | 'search'
      | 'my_location'
      | 'all_hilti_centers'
      | 'all_hilti_centers_results'
      | 'click_overlay'
      | 'click_offcanvas'
      | 'open_offcanvas';
    event_details?: string;
    hilti_centers?: string;
  };
  social_sharing: {
    event_method: string;
  };
  page_scrolled: {
    percentage_scrolled: number;
  };
  click_button: {
    button_type:
      | 'property_buttons'
      | 'click_order_button'
      | 'click_buy_button'
      | 'click_quote_button'
      | 'smart_bar'
      | 'click_fleet_button'
      | 'grid_page_switch'
      | 'tertiary'
      | 'close';
  };
  off_canvas: {
    button_type: 'open' | 'close';
  };
  main_navigation: {
    navigation_element: string;
  };
  tabs: {
    button_type: string;
    button_text: string;
  };
  auto_logout: {
    logout_type: 'button_login' | 'close' | 'message';
  };
  impression: {
    impression_method: 'product_options_dropdown';
  };
  delivery: {
    delivery_method: 'expected' | 'default';
    delivery_detail: string;
  };
  quick_access: {
    event_method: string;
  };
  trade_content: {
    event_method: string;
  };
  address_confirmation: {
    event_method: 'open_overlay' | 'close_overlay' | 'select_suggested' | 'select_original';
  };
  ids_checkout: {
    event_method: 'return_cart_back';
  };
  create_new_user: {
    event_method: 'open_overlay' | 'close_overlay';
  };
  select_products: {
    event_method: string;
    filter_title: string;
  };
  submittals: {
    event_phase: 'create' | 'generate' | 'save';
  };
  open_overlay: {
    event_details: string;
    overlay_type: 'small_module' | 'large_module';
  };
  close_overlay: {
    event_details: string;
  };
  write_review_submitted: {
    event_details: string;
  };
}
